import { defaultOutsourceUnit } from "@/lib/outsource-unit";
import React from "react";
import { formatDate, formatMoney, formatNumber } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import { ISOPrintHeader, PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface OutsourcePOPrintTemplateProps {
    po?: any;
    data?: any;
    config?: any;
}

export default function OutsourcePOPrintTemplate({ po, data, config }: OutsourcePOPrintTemplateProps) {
    const d = po || data || {};
    const c = getCompanyConfig(config);
    const now = new Date();
    const docId = d?.poId || d?.code || (d?.id ? `GC-${d.id.slice(0, 8)}` : "");
    const items: any[] = Array.isArray(d.materialIssued) ? d.materialIssued : [];
    // Công đoạn gửi gia công + ĐVT số lượng (tờ / pcs) — FB-1PZLNP, FB-CYG54A
    const stepChain: string = (d.outsourceSteps || []).map((st: any) => st.name).join(" → ") || d.process?.name || "";
    const stepUnit = defaultOutsourceUnit(d.outsourceSteps || []);
    const totalAmount = Number(d.totalAmount) || items.reduce((s: number, it: any) => s + (Number(it.amount) || 0), 0);
    const percent = d.qtyOrdered > 0 ? Math.round(((d.qtyReceived || 0) / d.qtyOrdered) * 100) : 0;

    return (
        <div 
            className="p-[10mm] text-black leading-relaxed text-[12px] bg-white w-full h-full mx-auto" 
            style={{ fontFamily: "'Times New Roman', Times, serif" }} 
            id="print-area"
        >
            {/* 1. HEADER CÔNG TY */}
            <ISOPrintHeader 
                config={config} 
            />

            {/* 2. TIÊU ĐỀ BIỂU MẪU (CANH GIỮA, KHÔNG QR) */}
            <PrintDocumentTitle
                title="ĐƠN ĐẶT GIA CÔNG NGOÀI"
                subtitle="OUTSOURCE PURCHASE ORDER"
                docNo={docId}
                date={d.issueDate || d.createdAt || now}
                dateLabel="Ngày lập"
                extraMeta={
                    (d.order?.orderId || d.productionOrder?.orderId) ? (
                        <span>
                            ĐH gốc: <strong className="font-mono text-black">{d.order?.orderId || d.productionOrder?.orderId}</strong>
                        </span>
                    ) : null
                }
            />

            {/* 3. THÔNG TIN ĐỐI TÁC GIA CÔNG (LƯỚI 2 CỘT TABS) */}
            <PrintInfoGrid
                leftColumn={[
                    {
                        label: "Đơn vị gia công",
                        labelEn: "Vendor",
                        value: d.vendor?.name || '—',
                        valueClassName: "font-bold text-slate-900",
                    },
                    {
                        label: "Địa chỉ xưởng",
                        labelEn: "Address",
                        value: d.vendor?.address || '—',
                    },
                    {
                        label: "Khách hàng/ĐH",
                        labelEn: "Order Ref",
                        value: d.order?.customer?.name || d.customerName || 'Nội bộ APV',
                    },
                    {
                        label: "Ngày xuất hàng",
                        labelEn: "Issue Date",
                        value: formatDate(d.issueDate || d.createdAt || now),
                    },
                ]}
                rightColumn={[
                    {
                        label: "Mã đối tác",
                        labelEn: "Vendor Code",
                        value: d.vendor?.code || '—',
                        valueClassName: "font-mono",
                    },
                    {
                        label: "Điện thoại / LH",
                        labelEn: "Contact",
                        value: d.vendor?.phone || d.vendor?.contactPerson || '—',
                    },
                    {
                        label: "Mã tham chiếu PO",
                        labelEn: "PO Ref",
                        value: d.poReference || '—',
                        valueClassName: "font-mono",
                    },
                    {
                        label: "Hạn hoàn thành",
                        labelEn: "Deadline",
                        value: formatDate(d.expectedDate || d.deliveryDate),
                        valueClassName: "text-red-700 font-bold",
                    },
                ]}
            />


            {/* 4. BẢNG CÔNG ĐOẠN GIA CÔNG */}
            <div className="mb-4">
                <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase">
                    2. NỘI DUNG CÔNG ĐOẠN GIA CÔNG CHI TIẾT
                </div>
                <table className="w-full border-collapse border border-black border-t-0 mb-1 text-[11px] table-fixed font-sans">
                    <thead>
                        <tr className="bg-slate-50 font-bold text-center border-b border-black">
                            <th className="border-r border-black px-1 py-1.5" style={{ width: '5%' }}>STT</th>
                            <th className="border-r border-black px-2 py-1.5 text-left" style={{ width: '32%' }}>Tên sản phẩm / Chi tiết gia công</th>
                            <th className="border-r border-black px-2 py-1.5 text-left" style={{ width: '23%' }}>Quy cách & Công đoạn</th>
                            <th className="border-r border-black px-1 py-1.5 text-center" style={{ width: '10%' }}>Số lượng</th>
                            <th className="border-r border-black px-1 py-1.5 text-right" style={{ width: '14%' }}>Đơn giá (VNĐ)</th>
                            <th className="px-1 py-1.5 text-right" style={{ width: '16%' }}>Thành tiền (VNĐ)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length > 0 ? items.map((item: any, index: number) => (
                            <tr key={index} className="border-b border-black last:border-b-0 break-inside-avoid">
                                <td className="border-r border-black px-1 py-1.5 text-center font-mono">{index + 1}</td>
                                <td className="border-r border-black px-2 py-1.5 text-left">
                                    <div className="font-semibold">
                                        {item.productCode && item.productCode !== item.productName ? <span className="font-mono">[{item.productCode}] </span> : null}
                                        {item.productName || item.name || '—'}
                                    </div>
                                    {stepChain ? <div className="text-[10px] italic mt-0.5">Gia công: {stepChain}</div> : null}
                                    {item.notes ? <div className="text-[10px] mt-0.5 whitespace-pre-wrap" data-testid="outsource-print-line-note">Ghi chú: {item.notes}</div> : null}
                                </td>
                                <td className="border-r border-black px-2 py-1.5 text-left">{item.productSpecs || item.processName || item.specs || '—'}</td>
                                <td className="border-r border-black px-1 py-1.5 text-center font-mono font-bold">
                                    <div className="whitespace-nowrap">{formatNumber(item.quantity || item.qty)}</div>
                                    <div className="font-sans font-normal text-[10px]">{item.unit || stepUnit}</div>
                                </td>
                                <td className="border-r border-black px-1 py-1.5 text-right font-mono">{Number(item.unitPrice) > 0 ? formatMoney(item.unitPrice) : '—'}</td>
                                <td className="px-1 py-1.5 text-right font-bold font-mono">{Number(item.amount) > 0 ? formatMoney(item.amount) : '—'}</td>
                            </tr>
                        )) : (
                            <tr className="border-b border-black">
                                <td className="border-r border-black px-1 py-1.5 text-center font-mono">1</td>
                                <td className="border-r border-black px-2 py-1.5 text-left font-semibold">{d.productName || 'Sản phẩm gia công'}</td>
                                <td className="border-r border-black px-2 py-1.5 text-left">{d.productSpecs || d.processName || '—'}</td>
                                <td className="border-r border-black px-1 py-1.5 text-center font-mono font-bold">{formatNumber(d.qtyOrdered || d.quantity || 1)}</td>
                                <td className="border-r border-black px-1 py-1.5 text-right font-mono">{formatMoney(d.unitPrice || 0)}</td>
                                <td className="px-1 py-1.5 text-right font-bold font-mono">{formatMoney(d.totalAmount || 0)}</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="bg-amber-50 font-bold border-t border-black">
                            <td colSpan={5} className="border-r border-black px-2 py-1.5 text-left text-[12px] uppercase">
                                TỔNG CỘNG GIÁ TRỊ GIA CÔNG (CHƯA VAT):
                            </td>
                            <td className="px-2 py-1.5 text-right text-[12px] text-red-700 font-mono">
                                {formatMoney(totalAmount)}
                            </td>
                        </tr>
                        <tr className="border-t border-black">
                            <td colSpan={6} className="px-2 py-1.5 text-[11px] italic">
                                <b>Số tiền viết bằng chữ:</b>{' '}
                                {numberToVietnameseWords(totalAmount, 'đồng')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* 5. TIẾN ĐỘ GIAO NHẬN (NẾU ĐÃ CÓ GIAO NHẬN TỪNG PHẦN) */}
            {(d.qtyReceived > 0 || d.qtyAccepted > 0 || d.qtyRejected > 0) && (
                <div className="mb-4">
                    <div className="bg-slate-100 font-bold text-[11px] px-2 py-1 border border-black uppercase">
                        3. TIẾN ĐỘ GIAO NHẬN & CHẤT LƯỢNG NGHIỆM THU
                    </div>
                    <table className="w-full border-collapse border border-black border-t-0 text-[11px] font-sans">
                        <thead>
                            <tr className="bg-slate-50 font-bold text-center border-b border-black text-[10px]">
                                <th className="border-r border-black px-2 py-1">SL Đặt</th>
                                <th className="border-r border-black px-2 py-1">SL Đã Giao</th>
                                <th className="border-r border-black px-2 py-1">Đạt Chuẩn (QC)</th>
                                <th className="border-r border-black px-2 py-1">Sai Hỏng / Trả</th>
                                <th className="px-2 py-1">Tỷ Lệ Hoàn Thành</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td className="border-r border-black px-2 py-1 font-mono">{formatNumber(d.qtyOrdered)}</td>
                                <td className="border-r border-black px-2 py-1 font-mono">{formatNumber(d.qtyReceived)}</td>
                                <td className="border-r border-black px-2 py-1 font-mono text-emerald-700 font-bold">{formatNumber(d.qtyAccepted)}</td>
                                <td className="border-r border-black px-2 py-1 font-mono text-red-700 font-bold">{formatNumber(d.qtyRejected)}</td>
                                <td className="px-2 py-1 font-bold">{percent}%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* Ghi chú cho nhà gia công (ô Ghi chú cho NCC của đơn) */}
            {d.notes && (
                <div className="mb-4 border border-black px-2 py-1.5 text-[11px] font-sans whitespace-pre-line" data-testid="outsource-print-notes">
                    <b>Ghi chú:</b> {d.notes}
                </div>
            )}

            {/* 6. CHỮ KÝ 4 BÊN */}
            <div className="grid grid-cols-4 gap-2 text-center mt-6 break-inside-avoid text-[12px] font-sans">
                <div>
                    <div className="font-bold uppercase">NGƯỜI LẬP LỆNH</div>
                    <div className="italic text-slate-500 text-[11px] mb-14">(Ký & ghi rõ họ tên)</div>
                    <div className="font-semibold">{d.createdBy || d.creator?.fullName || 'Phòng Mua Hàng'}</div>
                </div>
                <div>
                    <div className="font-bold uppercase">QUẢN LÝ SẢN XUẤT</div>
                    <div className="italic text-slate-500 text-[11px] mb-14">(Xác nhận tiến độ)</div>
                    <div className="font-semibold">&nbsp;</div>
                </div>
                <div>
                    <div className="font-bold uppercase">ĐƠN VỊ GIA CÔNG</div>
                    <div className="italic text-slate-500 text-[11px] mb-14">(Ký nhận & cam kết)</div>
                    <div className="font-semibold">{d.vendor?.name || '—'}</div>
                </div>
                <div>
                    <div className="font-bold uppercase">BAN GIÁM ĐỐC</div>
                    <div className="italic text-slate-500 text-[11px] mb-14">(Phê duyệt)</div>
                    <div className="font-semibold">&nbsp;</div>
                </div>
            </div>
        </div>
    );
}
